<template>
    <div class="main">
        <c-panel title="AGENDA ESCOLAR" titleIcon="fas fa-calendar-day">

            <!--
                BLOCO C — item 80: agenda com público-alvo.

                O professor cai aqui já com o filtro "professores" aplicado — o próprio
                backend decide isso quando nenhum público é enviado (tipo == 2). A tela
                não replica essa decisão; ela apenas mostra qual filtro voltou em
                `dados.publico_alvo` e permite trocar para quem tem outro perfil.
            -->
            <el-form label-position="top" @submit.native.prevent>
                <el-row :gutter="10" class="u-mb-15">
                    <el-col :xs="24" :sm="8">
                        <el-form-item label="Escola" class="u-mb-0">
                            <el-select v-model="instituicaoId" filterable style="width: 100%;" @change="carregar">
                                <el-option v-for="i in instituicoes" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="6">
                        <el-form-item label="Período letivo" class="u-mb-0">
                            <el-select v-model="periodoId" style="width: 100%;" @change="carregar">
                                <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="5">
                        <el-form-item label="Público-alvo" class="u-mb-0">
                            <el-select v-model="publico" clearable placeholder="Todos" style="width: 100%;" @change="carregar">
                                <el-option v-for="p in publicos" :key="p.valor" :value="p.valor" :label="p.rotulo"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="5">
                        <el-radio-group v-model="visao" size="small" class="u-mt-25">
                            <el-radio-button label="dia">Dia</el-radio-button>
                            <el-radio-button label="semana">Semana</el-radio-button>
                            <el-radio-button label="mes">Mês</el-radio-button>
                            <el-radio-button label="ano">Ano</el-radio-button>
                            <el-radio-button label="lista">Eventos</el-radio-button>
                        </el-radio-group>
                    </el-col>
                </el-row>
            </el-form>

            <el-alert v-if="dados && dados.erro" type="warning" :closable="false" :title="dados.erro"></el-alert>

            <div v-else-if="dados">
                <!--
                    ITENS 81 e 113: navegação de referência. Só faz sentido nas visões que
                    recortam um pedaço do calendário — na de ano e na lista, o recorte é o
                    período letivo inteiro.
                -->
                <div v-if="['dia', 'semana', 'mes'].includes(visao)" class="nav-data u-mb-10">
                    <el-button size="mini" icon="el-icon-arrow-left" @click="navegar(-1)"></el-button>
                    <span class="rot">{{ rotuloReferencia }}</span>
                    <el-button size="mini" icon="el-icon-arrow-right" @click="navegar(1)"></el-button>
                    <el-button size="mini" plain class="u-ml-10" @click="irParaHoje">HOJE</el-button>
                </div>

                <el-alert type="info" :closable="false" class="u-mb-15"
                    :title="dados.publico_alvo ? ('Exibindo o que se destina a: ' + rotuloPublico(dados.publico_alvo)) : 'Exibindo todos os públicos.'"
                    description="Itens sem público-alvo definido aparecem para todos."></el-alert>

                <!-- ==================== VISÃO LISTA ==================== -->
                <div v-if="visao === 'lista'">
                    <h4 class="u-mb-10">Eventos do calendário ({{ dados.eventos.length }})</h4>
                    <div class="container-table">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th style="width: 190px;">Período</th>
                                    <th>Evento</th>
                                    <th style="width: 150px;">Tipo</th>
                                    <th style="width: 200px;">Público-alvo</th>
                                    <th style="width: 110px;" class="u-text-center">Letivo</th>
                                    <th style="width: 110px;" class="u-text-center">Obrigatório</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="e in dados.eventos" :key="e.id">
                                    <td>{{ dataBr(e.data_inicio) }}<span v-if="e.data_fim !== e.data_inicio"> a {{ dataBr(e.data_fim) }}</span></td>
                                    <td>
                                        {{ e.nome }}
                                        <div v-if="e.descricao"><small class="u-text-muted">{{ e.descricao }}</small></div>
                                    </td>
                                    <td>{{ rotuloTipo(e.tipo) }}</td>
                                    <td>
                                        <el-tag v-for="p in (e.publico_alvo || [])" :key="p" size="mini" class="u-mr-5">{{ rotuloPublico(p) }}</el-tag>
                                        <small v-if="!e.publico_alvo || !e.publico_alvo.length" class="u-text-muted">todos</small>
                                    </td>
                                    <td class="u-text-center">
                                        <el-tag :type="e.dia_letivo ? 'success' : 'info'" size="mini">{{ e.dia_letivo ? 'sim' : 'não' }}</el-tag>
                                    </td>
                                    <td class="u-text-center">
                                        <el-tag v-if="e.obrigatorio" type="danger" size="mini">sim</el-tag>
                                        <span v-else>—</span>
                                    </td>
                                </tr>
                                <tr v-if="dados.eventos.length === 0"><td colspan="6" class="u-text-center">Nenhum evento para este filtro.</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <h4 class="u-mt-25 u-mb-10">Agenda ({{ dados.agenda.length }})</h4>
                    <div class="container-table">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th style="width: 190px;">Data</th>
                                    <th>Título</th>
                                    <th style="width: 200px;">Público-alvo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="a in dados.agenda" :key="a.id">
                                    <td>{{ dataBr(a.data) }}<span v-if="a.data_fim && a.data_fim !== a.data"> a {{ dataBr(a.data_fim) }}</span></td>
                                    <td>
                                        {{ a.titulo }}
                                        <div v-if="a.texto"><small class="u-text-muted">{{ a.texto }}</small></div>
                                    </td>
                                    <td>
                                        <el-tag v-for="p in (a.publico_alvo || [])" :key="p" size="mini" class="u-mr-5">{{ rotuloPublico(p) }}</el-tag>
                                        <small v-if="!a.publico_alvo || !a.publico_alvo.length" class="u-text-muted">todos</small>
                                    </td>
                                </tr>
                                <tr v-if="dados.agenda.length === 0"><td colspan="3" class="u-text-center">Nada na agenda para este filtro.</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- ==================== VISÃO DIA ==================== -->
                <div v-else-if="visao === 'dia'">
                    <div class="dia-bloco">
                        <div class="dia-cabecalho">{{ rotuloDiaCompleto(referencia) }}</div>
                        <div v-if="itensDoDia.length === 0" class="vazio">Nada marcado para este dia.</div>
                        <div v-for="(it, k) in itensDoDia" :key="'d' + k" class="item-linha">
                            <el-tag size="mini" :type="it.origem === 'evento' ? 'warning' : 'info'">{{ it.origem }}</el-tag>
                            <span class="titulo">{{ it.titulo }}</span>
                            <small v-if="it.descricao" class="u-text-muted">{{ it.descricao }}</small>
                        </div>
                    </div>
                </div>

                <!-- ==================== VISÃO SEMANA ==================== -->
                <div v-else-if="visao === 'semana'">
                    <div class="grade-semana">
                        <div v-for="d in diasDaSemana" :key="d.data" class="semana-col"
                             :class="{ 'semana-hoje': d.data === hojeIso }">
                            <div class="semana-titulo">
                                {{ d.rotulo }}<br><span class="semana-data">{{ dataBr(d.data) }}</span>
                            </div>
                            <div v-if="d.itens.length === 0" class="vazio">—</div>
                            <div v-for="(it, k) in d.itens" :key="'s' + k" class="semana-item"
                                 :class="it.origem === 'evento' ? 'is-evento' : 'is-agenda'">
                                {{ it.titulo }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ==================== VISÃO MÊS ==================== -->
                <div v-else-if="visao === 'mes'">
                    <div class="grade-mes">
                        <div v-for="r in ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']" :key="'h' + r" class="mes-cab">{{ r }}</div>
                        <div v-for="(c, k) in celulasDoMes" :key="'c' + k" class="mes-celula"
                             :class="{ 'fora': !c.noMes, 'hoje': c.data === hojeIso }">
                            <div class="mes-dia">{{ c.dia }}</div>
                            <div v-for="(it, j) in c.itens" :key="'m' + j" class="mes-marca"
                                 :class="it.origem === 'evento' ? 'is-evento' : 'is-agenda'" :title="it.titulo">
                                {{ it.titulo }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ==================== VISÃO ANO ==================== -->
                <div v-else>
                    <div class="grade-meses">
                        <div v-for="m in meses" :key="m.chave" class="mes-bloco">
                            <div class="mes-titulo">{{ m.rotulo }}</div>
                            <div v-if="m.itens.length === 0" class="mes-vazio">—</div>
                            <div v-for="(it, k) in m.itens" :key="k" class="mes-item">
                                <span class="dia">{{ it.dia }}</span> {{ it.titulo }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="carregando" class="u-text-center u-mt-25"><img src="/public/img/loading-bar.gif" /></div>

            <!--
                Sem escola selecionada não há o que buscar: `getAgendaProfessor` é por
                instituição. Perfis de rede (tipo 4/5) entram sem escola no contexto, e antes
                a tela ficava no gif de carregamento para sempre — parecia travada, mas
                nenhuma requisição chegou a sair.
            -->
            <el-alert v-else type="info" :closable="false"
                title="Selecione a escola para ver a agenda."
                description="A agenda é montada por escola e período letivo."></el-alert>
        </c-panel>
    </div>
</template>
<script>
/** BLOCO C — item 80: agenda por público-alvo, com o professor já filtrado. */
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    data: () => ({
        instituicaoId: null, periodoId: null, publico: null, visao: 'lista',
        referencia: null,
        instituicoes: [], dados: null, carregando: false,
        publicos: [
            { valor: 'alunos', rotulo: 'Alunos' },
            { valor: 'professores', rotulo: 'Professores' },
            { valor: 'gestores', rotulo: 'Gestores' },
            { valor: 'pais', rotulo: 'Pais e responsáveis' },
            { valor: 'servidores', rotulo: 'Servidores' }
        ]
    }),
    computed: {
        /** Agrupa eventos e agenda por mês, para a visão de calendário resumido. */
        hojeIso () { return this.$moment().format('YYYY-MM-DD') },
        /**
         * ITENS 81 e 113 — todos os itens do calendário achatados em uma lista por data.
         *
         * Evento com data_inicio/data_fim ocupa TODOS os dias do intervalo: um recesso de
         * uma semana precisa aparecer na quarta-feira, não só no dia em que começou.
         */
        itensPorData () {
            const mapa = {}
            if (!this.dados) return mapa

            const marcar = (ini, fim, titulo, origem, descricao) => {
                if (!ini) return
                let d = this.$moment(String(ini).substring(0, 10))
                const ate = this.$moment(String(fim || ini).substring(0, 10))
                if (!d.isValid() || !ate.isValid()) return

                // Trava de segurança: intervalo absurdo não trava o navegador.
                let guarda = 0
                while (d.isSameOrBefore(ate) && guarda < 400) {
                    const chave = d.format('YYYY-MM-DD')
                    if (!mapa[chave]) mapa[chave] = []
                    mapa[chave].push({ titulo, origem, descricao })
                    d = d.add(1, 'day')
                    guarda++
                }
            }

            this.dados.eventos.forEach(e => marcar(e.data_inicio, e.data_fim, e.nome, 'evento', e.descricao))
            this.dados.agenda.forEach(a => marcar(a.data, a.data_fim, a.titulo, 'agenda', a.texto))

            return mapa
        },
        itensDoDia () {
            return this.itensPorData[this.referencia] || []
        },
        diasDaSemana () {
            const rotulos = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
            const inicio = this.$moment(this.referencia).startOf('week')
            const saida = []

            for (let i = 0; i < 7; i++) {
                const d = this.$moment(inicio).add(i, 'day')
                const iso = d.format('YYYY-MM-DD')
                saida.push({ data: iso, rotulo: rotulos[i], itens: this.itensPorData[iso] || [] })
            }

            return saida
        },
        celulasDoMes () {
            const ref = this.$moment(this.referencia)
            const mes = ref.month()
            const inicio = this.$moment(ref).startOf('month').startOf('week')
            const fim = this.$moment(ref).endOf('month').endOf('week')
            const saida = []
            let d = this.$moment(inicio)
            let guarda = 0

            while (d.isSameOrBefore(fim) && guarda < 45) {
                const iso = d.format('YYYY-MM-DD')
                saida.push({
                    data: iso, dia: d.format('D'), noMes: d.month() === mes,
                    itens: this.itensPorData[iso] || []
                })
                d = this.$moment(d).add(1, 'day')
                guarda++
            }

            return saida
        },
        rotuloReferencia () {
            if (!this.referencia) return ''
            const d = this.$moment(this.referencia)
            if (this.visao === 'dia') return d.format('DD/MM/YYYY')
            if (this.visao === 'semana') {
                const i = this.$moment(d).startOf('week')
                const f = this.$moment(d).endOf('week')
                return i.format('DD/MM') + ' a ' + f.format('DD/MM/YYYY')
            }
            const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
            return nomes[d.month()] + ' de ' + d.format('YYYY')
        },
        meses () {
            if (!this.dados) return []

            const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
            const mapa = {}

            const por = (data, titulo) => {
                if (!data) return
                const d = data.substring(0, 10)
                const chave = d.substring(0, 7)
                if (!mapa[chave]) mapa[chave] = []
                mapa[chave].push({ dia: d.substring(8, 10), titulo })
            }

            this.dados.eventos.forEach(e => por(e.data_inicio, e.nome))
            this.dados.agenda.forEach(a => por(a.data, a.titulo))

            return Object.keys(mapa).sort().map(chave => ({
                chave,
                rotulo: nomes[parseInt(chave.substring(5, 7), 10) - 1] + '/' + chave.substring(0, 4),
                itens: mapa[chave].sort((a, b) => a.dia.localeCompare(b.dia))
            }))
        }
    },
    mounted () {
        this.referencia = this.$moment().format('YYYY-MM-DD')
        this.periodoId = this.$store.state.config.periodo ? this.$store.state.config.periodo.id : null

        const inst = this.$store.state.config.instituicao
        if (inst && inst.id) { this.instituicoes = [inst]; this.instituicaoId = inst.id }

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`,
            this.$store.getters.getHeaderAuth
        ).then(r => {
            if (Array.isArray(r.data) && r.data.length) this.instituicoes = r.data
        }).catch(() => {}).then(() => this.carregar())
    },
    methods: {
        navegar (passo) {
            const unidade = this.visao === 'dia' ? 'day' : (this.visao === 'semana' ? 'week' : 'month')
            this.referencia = this.$moment(this.referencia).add(passo, unidade).format('YYYY-MM-DD')
        },
        irParaHoje () { this.referencia = this.$moment().format('YYYY-MM-DD') },
        rotuloDiaCompleto (iso) {
            const dias = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']
            const d = this.$moment(iso)
            return dias[d.day()] + ', ' + d.format('DD/MM/YYYY')
        },
        carregar () {
            this.dados = null
            if (!this.instituicaoId || !this.periodoId) { this.carregando = false; return }
            this.carregando = true

            const url = `${this.$store.state.config.baseUrlApi}/api/evento-calendario/getAgendaProfessor/${this.instituicaoId}/${this.periodoId}`
            const cfg = JSON.parse(JSON.stringify(this.$store.getters.getHeaderAuth))
            cfg.params = this.publico ? { publico_alvo: this.publico } : {}

            this.$axios.get(url, cfg)
                .then(r => { this.carregando = false; this.dados = r.data })
                .catch(e => { this.carregando = false; this.$store.commit('throwException', e) })
        },
        rotuloPublico (v) {
            const p = this.publicos.find(x => x.valor === v)
            return p ? p.rotulo : v
        },
        rotuloTipo (t) {
            return {
                feriado_nacional: 'Feriado nacional', feriado_estadual: 'Feriado estadual',
                feriado_municipal: 'Feriado municipal', recesso: 'Recesso',
                ponto_facultativo: 'Ponto facultativo', paralisacao: 'Paralisação',
                evento_escolar: 'Evento escolar', avaliacao: 'Avaliação',
                formacao: 'Formação', reuniao: 'Reunião', outros: 'Outros'
            }[t] || t
        },
        dataBr (d) {
            if (!d) return '—'
            const p = d.substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        }
    }
}
</script>
<style scoped>
.grade-meses { display: flex; flex-wrap: wrap; }
.mes-bloco { width: 25%; min-width: 220px; padding: 8px; box-sizing: border-box; }
.mes-titulo { font-weight: bold; border-bottom: 1px solid #dcdfe6; padding-bottom: 4px; margin-bottom: 6px; }
.mes-item { font-size: 12px; padding: 2px 0; }
.mes-item .dia { display: inline-block; width: 24px; font-weight: bold; color: #409EFF; }
.mes-vazio { font-size: 12px; color: #909399; }

/* ITENS 81 e 113 — visões dia, semana, mês e ano */
.nav-data { display: flex; align-items: center; }
.nav-data .rot { margin: 0 12px; font-weight: bold; color: #303133; min-width: 190px; text-align: center; }
.vazio { color: #909399; font-size: 12px; padding: 4px 0; }

.dia-bloco { border: 1px solid #ebeef5; border-radius: 4px; padding: 12px; }
.dia-cabecalho { font-weight: bold; margin-bottom: 10px; color: #303133; }
.item-linha { padding: 6px 0; border-bottom: 1px dashed #ebeef5; }
.item-linha .titulo { margin-left: 8px; }

.grade-semana { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 6px; }
.semana-col { border: 1px solid #ebeef5; border-radius: 4px; padding: 6px; min-height: 110px; }
.semana-col.semana-hoje { border-color: #409eff; }
.semana-titulo { font-size: 11px; font-weight: bold; color: #606266; margin-bottom: 6px; }
.semana-data { font-weight: normal; color: #909399; }
.semana-item { font-size: 11px; padding: 3px 4px; border-radius: 3px; margin-bottom: 3px; word-break: break-word; }

.grade-mes { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 4px; }
.mes-cab { font-size: 11px; font-weight: bold; text-align: center; color: #909399; padding-bottom: 4px; }
.mes-celula { border: 1px solid #ebeef5; border-radius: 3px; min-height: 78px; padding: 4px; }
.mes-celula.fora { background: #fafafa; color: #c0c4cc; }
.mes-celula.hoje { border-color: #409eff; }
.mes-dia { font-size: 11px; font-weight: bold; color: #606266; }
.mes-marca { font-size: 10px; padding: 2px 3px; border-radius: 2px; margin-top: 2px;
             overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.is-evento { background: #fdf6ec; color: #b88230; }
.is-agenda { background: #ecf5ff; color: #337ecc; }

@media (max-width: 768px) {
  .grade-semana, .grade-mes { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .mes-cab { display: none; }
}
</style>
